import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Table,
  Button,
  Form,
  Row,
  Col,
  Modal,
  Pagination,
  Badge,
  Spinner,
  ButtonGroup,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Alert,
} from 'react-bootstrap';
import {
  FaSyncAlt,
  FaTrash,
  FaEdit,
  FaSearch,
  FaStore,
  FaShoppingCart,
  FaWarehouse,
  FaLayerGroup,
  FaArchive,
  FaCheckCircle,
  FaTimesCircle,
} from 'react-icons/fa';
import api from '../api';

const pageStyle = {
  minHeight: '100vh',
  background:
    'radial-gradient(circle at top left, rgba(59,130,246,0.20), transparent 32%), radial-gradient(circle at top right, rgba(34,197,94,0.12), transparent 28%), linear-gradient(180deg, #020617 0%, #0f172a 42%, #111827 100%)',
  color: '#e5e7eb',
};

const shellStyle = {
  maxWidth: 1900,
  margin: '0 auto',
};

const cardStyle = {
  background: 'rgba(15, 23, 42, 0.86)',
  border: '1px solid rgba(148, 163, 184, 0.16)',
  borderRadius: 22,
  color: '#e5e7eb',
  boxShadow: '0 18px 45px rgba(0,0,0,0.28)',
  backdropFilter: 'blur(10px)',
};

const cardSoftStyle = {
  background: 'rgba(30, 41, 59, 0.82)',
  border: '1px solid rgba(148, 163, 184, 0.14)',
  borderRadius: 20,
  color: '#e5e7eb',
};

const heroStyle = {
  background:
    'linear-gradient(135deg, rgba(37,99,235,0.35), rgba(15,23,42,0.94) 42%, rgba(34,197,94,0.20))',
  border: '1px solid rgba(148, 163, 184, 0.18)',
  borderRadius: 28,
  color: '#f8fafc',
  boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
};

const inputStyle = {
  background: '#020617',
  color: '#e5e7eb',
  border: '1px solid rgba(148, 163, 184, 0.35)',
  borderRadius: 12,
};

const mutedStyle = {
  color: '#94a3b8',
};

const pillStyle = {
  border: '1px solid rgba(148, 163, 184, 0.20)',
  background: 'rgba(15, 23, 42, 0.72)',
  color: '#cbd5e1',
  borderRadius: 999,
  padding: '6px 11px',
  fontSize: 12,
  fontWeight: 700,
};

const cleanNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatMoney = (value) => {
  const num = cleanNumber(value);

  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

function Listings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshingAction, setRefreshingAction] = useState(false);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    platform: '',
    q: '',
    status: '',
    active: '',
  });

  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);

  const [editItem, setEditItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const visibleActiveCount = useMemo(() => {
    return listings.filter((item) => Number(item.active) === 1).length;
  }, [listings]);

  const visibleSyncedCount = useMemo(() => {
    return listings.filter((item) => Number(item.synced) === 1 || item.synced === true).length;
  }, [listings]);

  const visibleArchivedCount = useMemo(() => {
    return listings.filter((item) => String(item.status || '').toUpperCase() === 'ARCHIVED').length;
  }, [listings]);

  const selectedCount = selected.length;

  useEffect(() => {
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  async function fetchListings(options = {}) {
    const { silent = false } = options;

    try {
      if (silent) {
        setRefreshingAction(true);
      } else {
        setLoading(true);
      }

      setError('');

      const params = new URLSearchParams({
        page,
        limit,
        ...filters,
      });

      const res = await api.get(`/listings?${params.toString()}`);

      setListings(res.data.listings || []);
      setTotal(res.data.total || 0);
      setSelected([]);
    } catch (err) {
      console.error('❌ Error fetching listings', err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Failed to load listings.'
      );
    } finally {
      setLoading(false);
      setRefreshingAction(false);
    }
  }

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      platform: '',
      q: '',
      status: '',
      active: '',
    });
    setPage(1);
  };

  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const selectAll = () =>
    setSelected(
      selected.length === listings.length ? [] : listings.map((x) => x.listing_id)
    );

  async function saveEdit() {
    if (!editItem) return;

    try {
      setRefreshingAction(true);

      await api.patch(`/listings/${editItem.listing_id}`, {
        title_override: editItem.title_override,
        description_override: editItem.description_override,
        price_override: editItem.price_override,
        active: editItem.active,
        status: editItem.status,
      });

      setShowModal(false);
      setEditItem(null);
      await fetchListings({ silent: true });
    } catch (err) {
      console.error('❌ Failed to save listing', err);
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Failed to save listing.'
      );
    } finally {
      setRefreshingAction(false);
    }
  }

  async function resyncListing(id) {
    try {
      setRefreshingAction(true);
      await api.post(`/listings/${id}/resync`);
      await fetchListings({ silent: true });
    } catch (err) {
      console.error('❌ Failed to resync', err);
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Failed to resync listing.'
      );
    } finally {
      setRefreshingAction(false);
    }
  }

  async function archiveListing(id) {
    if (!window.confirm('Archive this listing?')) return;

    try {
      setRefreshingAction(true);
      await api.delete(`/listings/${id}`);
      await fetchListings({ silent: true });
    } catch (err) {
      console.error('❌ Failed to archive', err);
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Failed to archive listing.'
      );
    } finally {
      setRefreshingAction(false);
    }
  }

  async function bulkResync() {
    if (!selected.length) return;

    try {
      setRefreshingAction(true);

      for (const id of selected) {
        await api.post(`/listings/${id}/resync`);
      }

      await fetchListings({ silent: true });
    } catch (err) {
      console.error('❌ Bulk resync failed', err);
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Bulk resync failed.'
      );
    } finally {
      setRefreshingAction(false);
    }
  }

  async function bulkArchive() {
    if (!selected.length) return;

    if (!window.confirm(`Archive ${selected.length} selected listing(s)?`)) return;

    try {
      setRefreshingAction(true);

      for (const id of selected) {
        await api.delete(`/listings/${id}`);
      }

      await fetchListings({ silent: true });
    } catch (err) {
      console.error('❌ Bulk archive failed', err);
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Bulk archive failed.'
      );
    } finally {
      setRefreshingAction(false);
    }
  }

  const platformBadge = (platform) => {
    const cleanPlatform = String(platform || '').toLowerCase();

    if (cleanPlatform === 'shopify') {
      return (
        <Badge
          bg=""
          style={{
            background: 'rgba(34,197,94,0.16)',
            color: '#86efac',
            border: '1px solid rgba(34,197,94,0.28)',
            borderRadius: 999,
            padding: '7px 10px',
          }}
        >
          <FaStore className="me-1" /> Shopify
        </Badge>
      );
    }

    if (cleanPlatform === 'ebay') {
      return (
        <Badge
          bg=""
          style={{
            background: 'rgba(59,130,246,0.16)',
            color: '#bfdbfe',
            border: '1px solid rgba(59,130,246,0.28)',
            borderRadius: 999,
            padding: '7px 10px',
          }}
        >
          <FaShoppingCart className="me-1" /> eBay
        </Badge>
      );
    }

    if (cleanPlatform === 'walmart') {
      return (
        <Badge
          bg=""
          style={{
            background: 'rgba(245,158,11,0.16)',
            color: '#fcd34d',
            border: '1px solid rgba(245,158,11,0.28)',
            borderRadius: 999,
            padding: '7px 10px',
          }}
        >
          <FaWarehouse className="me-1" /> Walmart
        </Badge>
      );
    }

    return (
      <Badge
        bg=""
        style={{
          background: 'rgba(148,163,184,0.14)',
          color: '#cbd5e1',
          border: '1px solid rgba(148,163,184,0.22)',
          borderRadius: 999,
          padding: '7px 10px',
        }}
      >
        {platform || 'Unknown'}
      </Badge>
    );
  };

  const statusBadge = (status) => {
    const cleanStatus = String(status || '').toUpperCase();

    if (cleanStatus === 'ACTIVE') {
      return (
        <Badge
          bg=""
          style={{
            background: 'rgba(34,197,94,0.16)',
            color: '#86efac',
            border: '1px solid rgba(34,197,94,0.28)',
            borderRadius: 999,
            padding: '7px 10px',
          }}
        >
          Active
        </Badge>
      );
    }

    if (cleanStatus === 'ARCHIVED') {
      return (
        <Badge
          bg=""
          style={{
            background: 'rgba(239,68,68,0.16)',
            color: '#fca5a5',
            border: '1px solid rgba(239,68,68,0.28)',
            borderRadius: 999,
            padding: '7px 10px',
          }}
        >
          Archived
        </Badge>
      );
    }

    return (
      <Badge
        bg=""
        style={{
          background: 'rgba(148,163,184,0.14)',
          color: '#cbd5e1',
          border: '1px solid rgba(148,163,184,0.22)',
          borderRadius: 999,
          padding: '7px 10px',
        }}
      >
        {status || 'Inactive'}
      </Badge>
    );
  };

  const syncPlatformBadges = (item) => {
    if (!item.platforms) {
      return (
        <Badge
          bg=""
          style={{
            background: 'rgba(148,163,184,0.14)',
            color: '#cbd5e1',
            border: '1px solid rgba(148,163,184,0.22)',
            borderRadius: 999,
          }}
        >
          Unlinked
        </Badge>
      );
    }

    return String(item.platforms)
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => {
        const clean = p.toLowerCase();

        let style = {
          background: 'rgba(148,163,184,0.14)',
          color: '#cbd5e1',
          border: '1px solid rgba(148,163,184,0.22)',
        };

        if (clean === 'ebay') {
          style = {
            background: 'rgba(59,130,246,0.16)',
            color: '#bfdbfe',
            border: '1px solid rgba(59,130,246,0.28)',
          };
        }

        if (clean === 'shopify') {
          style = {
            background: 'rgba(34,197,94,0.16)',
            color: '#86efac',
            border: '1px solid rgba(34,197,94,0.28)',
          };
        }

        if (clean === 'walmart') {
          style = {
            background: 'rgba(245,158,11,0.16)',
            color: '#fcd34d',
            border: '1px solid rgba(245,158,11,0.28)',
          };
        }

        return (
          <Badge
            key={p}
            bg=""
            className="me-1 mb-1"
            style={{
              ...style,
              borderRadius: 999,
              padding: '6px 9px',
            }}
          >
            {p}
          </Badge>
        );
      });
  };

  const MiniMetric = ({ label, value, color = '#e5e7eb', icon }) => {
    return (
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 16,
          background: 'rgba(2, 6, 23, 0.45)',
          border: '1px solid rgba(148, 163, 184, 0.12)',
        }}
      >
        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 3 }}>
          {icon && <span className="me-1">{icon}</span>}
          {label}
        </div>
        <div style={{ color, fontWeight: 850 }}>{value}</div>
      </div>
    );
  };

  const StatCard = ({ label, value, subtext, accent = '#38bdf8', icon = '📦', valueColor }) => {
    return (
      <div className="col-md-6 col-xl-3 mb-3">
        <div
          className="h-100"
          style={{
            ...cardStyle,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at top right, ${accent}33, transparent 36%)`,
              pointerEvents: 'none',
            }}
          />

          <div className="p-3 p-lg-4 position-relative">
            <div className="d-flex justify-content-between align-items-start gap-3">
              <div>
                <div
                  style={{
                    color: '#94a3b8',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                  }}
                >
                  {label}
                </div>

                <div
                  className="mt-2"
                  style={{
                    fontSize: 30,
                    lineHeight: 1.05,
                    fontWeight: 900,
                    color: valueColor || '#f8fafc',
                  }}
                >
                  {value}
                </div>
              </div>

              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 16,
                  background: `${accent}22`,
                  border: `1px solid ${accent}35`,
                  fontSize: 22,
                }}
              >
                {icon}
              </div>
            </div>

            {subtext && (
              <div className="mt-2" style={{ color: '#94a3b8', fontSize: 13 }}>
                {subtext}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const windowSize = 5;
    const start = Math.max(1, page - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    const adjustedStart = Math.max(1, end - windowSize + 1);
    const items = [];

    if (adjustedStart > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setPage(1)}>
          1
        </Pagination.Item>
      );

      if (adjustedStart > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let i = adjustedStart; i <= end; i += 1) {
      items.push(
        <Pagination.Item key={i} active={i === page} onClick={() => setPage(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }

      items.push(
        <Pagination.Item key={totalPages} onClick={() => setPage(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3">
        <div style={mutedStyle}>
          Page <strong style={{ color: '#f8fafc' }}>{page}</strong> of{' '}
          <strong style={{ color: '#f8fafc' }}>{totalPages}</strong>
        </div>

        <Pagination className="mb-0 listings-pagination">
          <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
          <Pagination.Prev
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          />
          {items}
          <Pagination.Next
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
          <Pagination.Last
            disabled={page === totalPages}
            onClick={() => setPage(totalPages)}
          />
        </Pagination>
      </div>
    );
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          .listings-dark input,
          .listings-dark select,
          .listings-dark textarea {
            background-color: #020617 !important;
            color: #e5e7eb !important;
            border-color: rgba(148, 163, 184, 0.35) !important;
          }

          .listings-dark input::placeholder,
          .listings-dark textarea::placeholder {
            color: #64748b !important;
          }

          .listings-dark input:focus,
          .listings-dark select:focus,
          .listings-dark textarea:focus {
            box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.18) !important;
            border-color: rgba(56, 189, 248, 0.75) !important;
          }

          .listings-dark .form-label {
            color: #cbd5e1;
            font-size: 13px;
            font-weight: 700;
          }

          .listings-dark .btn {
            border-radius: 12px;
            font-weight: 700;
          }

          .listings-dark .table {
            --bs-table-bg: transparent;
            --bs-table-color: #e5e7eb;
            --bs-table-border-color: rgba(148, 163, 184, 0.12);
            color: #e5e7eb;
            margin-bottom: 0;
          }

          .listings-dark .table thead th {
            color: #cbd5e1;
            background: rgba(15, 23, 42, 0.94) !important;
            border-bottom: 1px solid rgba(148, 163, 184, 0.18);
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.6px;
            white-space: nowrap;
            vertical-align: middle;
          }

          .listings-dark .table tbody td {
            vertical-align: middle;
            border-color: rgba(148, 163, 184, 0.10);
            white-space: nowrap;
          }

          .listings-dark .table tbody tr:hover {
            background: rgba(30, 41, 59, 0.48);
          }

          .listings-dark .pagination .page-link {
            background: rgba(15, 23, 42, 0.88);
            border-color: rgba(148, 163, 184, 0.18);
            color: #cbd5e1;
            font-weight: 700;
          }

          .listings-dark .pagination .page-item.active .page-link {
            background: #2563eb;
            border-color: #2563eb;
            color: #ffffff;
          }

          .listings-dark .pagination .page-item.disabled .page-link {
            background: rgba(15, 23, 42, 0.55);
            border-color: rgba(148, 163, 184, 0.10);
            color: #64748b;
          }

          .listings-modal .modal-content {
            background: #0f172a;
            border: 1px solid rgba(148, 163, 184, 0.18);
            border-radius: 22px;
            color: #e5e7eb;
            box-shadow: 0 24px 70px rgba(0,0,0,0.45);
          }

          .listings-modal .modal-header {
            border-bottom: 1px solid rgba(148, 163, 184, 0.14);
          }

          .listings-modal .modal-footer {
            border-top: 1px solid rgba(148, 163, 184, 0.14);
          }

          .listings-modal .btn-close {
            filter: invert(1) grayscale(100%) brightness(200%);
          }

          .listing-thumbnail {
            width: 62px;
            height: 62px;
            object-fit: contain;
            border-radius: 14px;
            border: 1px solid rgba(148, 163, 184, 0.22);
            background: rgba(2, 6, 23, 0.60);
            padding: 4px;
          }
        `}
      </style>

      <Container fluid className="py-4 listings-dark" style={shellStyle}>
        <div className="p-4 p-lg-5 mb-4" style={heroStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span style={{ ...pillStyle, color: '#bfdbfe', borderColor: 'rgba(59,130,246,0.38)' }}>
                  Listings
                </span>
                <span style={{ ...pillStyle, color: '#bbf7d0', borderColor: 'rgba(34,197,94,0.38)' }}>
                  Platform Sync
                </span>
                <span style={{ ...pillStyle, color: '#fef3c7', borderColor: 'rgba(245,158,11,0.38)' }}>
                  Bulk Tools
                </span>
                <span style={{ ...pillStyle, color: '#ddd6fe', borderColor: 'rgba(168,85,247,0.38)' }}>
                  {total.toLocaleString()} Total
                </span>
              </div>

              <h1
                className="mb-3"
                style={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  letterSpacing: '-0.04em',
                  fontSize: 'clamp(2rem, 4vw, 4.35rem)',
                  lineHeight: 0.95,
                }}
              >
                Listings Control Center
              </h1>

              <p className="mb-0" style={{ color: '#cbd5e1', maxWidth: 940, fontSize: 17 }}>
                View marketplace listings, search by SKU or title, check linked platforms,
                edit listing overrides, resync data, and archive listings from one clean page.
              </p>
            </div>

            <div
              className="p-3"
              style={{
                ...cardSoftStyle,
                minWidth: 330,
              }}
            >
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <div style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Current View
                  </div>

                  <div style={{ color: '#f8fafc', fontSize: 26, fontWeight: 900 }}>
                    Page {page} / {totalPages}
                  </div>
                </div>

                <OverlayTrigger overlay={<Tooltip>Refresh Listings</Tooltip>}>
                  <Button
                    variant="outline-info"
                    onClick={() => fetchListings({ silent: true })}
                    disabled={loading || refreshingAction}
                  >
                    {refreshingAction ? <Spinner size="sm" /> : <FaSyncAlt />}
                  </Button>
                </OverlayTrigger>
              </div>

              <Row className="g-2">
                <Col xs={6}>
                  <MiniMetric label="Showing" value={listings.length.toLocaleString()} color="#38bdf8" />
                </Col>
                <Col xs={6}>
                  <MiniMetric label="Selected" value={selectedCount.toLocaleString()} color="#c4b5fd" />
                </Col>
              </Row>
            </div>
          </div>
        </div>

        {error && (
          <Alert
            style={{
              background: 'rgba(127,29,29,0.70)',
              border: '1px solid rgba(248,113,113,0.35)',
              color: '#fee2e2',
              borderRadius: 18,
            }}
          >
            <strong>Listings could not load.</strong>
            <div>{error}</div>
            <Button size="sm" variant="light" className="mt-2" onClick={() => fetchListings()}>
              Retry
            </Button>
          </Alert>
        )}

        <Row className="mb-4">
          <StatCard
            label="Total Listings"
            value={total.toLocaleString()}
            subtext="Matching current filters"
            accent="#38bdf8"
            icon="📋"
          />

          <StatCard
            label="Visible Active"
            value={visibleActiveCount.toLocaleString()}
            subtext={`${listings.length.toLocaleString()} rows loaded on this page`}
            accent="#22c55e"
            icon="✅"
            valueColor="#86efac"
          />

          <StatCard
            label="Visible Synced"
            value={visibleSyncedCount.toLocaleString()}
            subtext="Synced rows on this page"
            accent="#a855f7"
            icon="🔄"
            valueColor="#c4b5fd"
          />

          <StatCard
            label="Visible Archived"
            value={visibleArchivedCount.toLocaleString()}
            subtext="Archived rows on this page"
            accent="#ef4444"
            icon="🗄️"
            valueColor="#fca5a5"
          />
        </Row>

        <div className="p-4 mb-4" style={cardStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                Filters
              </h4>
              <p className="mb-0" style={mutedStyle}>
                Filter by platform, listing status, active flag, or search title/SKU/description.
              </p>
            </div>

            <ButtonGroup>
              <Button variant="outline-light" onClick={resetFilters}>
                Reset
              </Button>

              {selectedCount > 0 && (
                <>
                  <Button variant="warning" onClick={bulkResync} disabled={refreshingAction}>
                    ⚙️ Resync ({selectedCount})
                  </Button>

                  <Button variant="outline-danger" onClick={bulkArchive} disabled={refreshingAction}>
                    🗑 Archive ({selectedCount})
                  </Button>
                </>
              )}
            </ButtonGroup>
          </div>

          <Form onSubmit={(e) => e.preventDefault()}>
            <Row className="gy-3 align-items-end">
              <Col xl={2} lg={3} md={6}>
                <Form.Label>Platform</Form.Label>
                <Form.Select
                  name="platform"
                  value={filters.platform}
                  onChange={handleFilterChange}
                  style={inputStyle}
                >
                  <option value="">All Platforms</option>
                  <option value="ebay">eBay</option>
                  <option value="shopify">Shopify</option>
                  <option value="walmart">Walmart</option>
                </Form.Select>
              </Col>

              <Col xl={2} lg={3} md={6}>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  style={inputStyle}
                >
                  <option value="">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="ARCHIVED">Archived</option>
                </Form.Select>
              </Col>

              <Col xl={2} lg={3} md={6}>
                <Form.Label>Active Flag</Form.Label>
                <Form.Select
                  name="active"
                  value={filters.active}
                  onChange={handleFilterChange}
                  style={inputStyle}
                >
                  <option value="">All</option>
                  <option value="1">Active Only</option>
                  <option value="0">Inactive Only</option>
                </Form.Select>
              </Col>

              <Col xl={5} lg={6} md={12}>
                <Form.Label>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      background: '#020617',
                      border: '1px solid rgba(148, 163, 184, 0.35)',
                      color: '#94a3b8',
                      borderRadius: '12px 0 0 12px',
                    }}
                  >
                    <FaSearch />
                  </InputGroup.Text>

                  <Form.Control
                    type="text"
                    name="q"
                    placeholder="Search by title, SKU, or description..."
                    value={filters.q}
                    onChange={handleFilterChange}
                    style={{
                      ...inputStyle,
                      borderRadius: 0,
                    }}
                  />

                  <Button variant="primary" onClick={() => fetchListings()}>
                    Search
                  </Button>
                </InputGroup>
              </Col>
            </Row>
          </Form>
        </div>

        <div className="p-4" style={cardStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                Marketplace Listings
              </h4>
              <p className="mb-0" style={mutedStyle}>
                Showing {listings.length.toLocaleString()} of {total.toLocaleString()} total.
              </p>
            </div>

            {refreshingAction && (
              <span style={{ ...pillStyle, color: '#bfdbfe' }}>
                <Spinner size="sm" className="me-2" />
                Refreshing
              </span>
            )}
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="info" />
              <div className="mt-3" style={mutedStyle}>
                Loading listings...
              </div>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover responsive className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}>
                        <Form.Check
                          type="checkbox"
                          checked={selected.length === listings.length && listings.length > 0}
                          onChange={selectAll}
                        />
                      </th>
                      <th>SKU</th>
                      <th>Thumbnail</th>
                      <th>Name</th>
                      <th>Platform</th>
                      <th>Status</th>
                      <th>Sync</th>
                      <th>Active</th>
                      <th className="text-end">Price</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {listings.length > 0 ? (
                      listings.map((item) => (
                        <tr key={item.listing_id}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={selected.includes(item.listing_id)}
                              onChange={() => toggleSelect(item.listing_id)}
                            />
                          </td>

                          <td>
                            <strong style={{ color: '#f8fafc' }}>{item.sku || '—'}</strong>
                          </td>

                          <td>
                            {item.images && item.images.length > 0 ? (
                              <img
                                src={item.images[0]}
                                alt="thumb"
                                className="listing-thumbnail"
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center listing-thumbnail"
                                style={{ color: '#64748b', fontSize: 12 }}
                              >
                                —
                              </div>
                            )}
                          </td>

                          <td style={{ minWidth: 320, whiteSpace: 'normal' }}>
                            <div style={{ color: '#e5e7eb', fontWeight: 850 }}>
                              {item.name || 'No Name'}
                            </div>

                            {item.title_override && (
                              <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
                                Override: {item.title_override}
                              </div>
                            )}
                          </td>

                          <td>{platformBadge(item.platform)}</td>

                          <td>{statusBadge(item.status)}</td>

                          <td style={{ minWidth: 170 }}>
                            <div className="mb-1">{syncPlatformBadges(item)}</div>

                            {item.synced ? (
                              <Badge
                                bg=""
                                style={{
                                  background: 'rgba(34,197,94,0.16)',
                                  color: '#86efac',
                                  border: '1px solid rgba(34,197,94,0.28)',
                                  borderRadius: 999,
                                }}
                              >
                                <FaCheckCircle className="me-1" />
                                Synced
                              </Badge>
                            ) : (
                              <Badge
                                bg=""
                                style={{
                                  background: 'rgba(148,163,184,0.14)',
                                  color: '#cbd5e1',
                                  border: '1px solid rgba(148,163,184,0.22)',
                                  borderRadius: 999,
                                }}
                              >
                                Pending
                              </Badge>
                            )}
                          </td>

                          <td>
                            {item.active ? (
                              <span style={{ color: '#86efac', fontWeight: 900 }}>
                                <FaCheckCircle className="me-1" />
                                Yes
                              </span>
                            ) : (
                              <span style={{ color: '#fca5a5', fontWeight: 900 }}>
                                <FaTimesCircle className="me-1" />
                                No
                              </span>
                            )}
                          </td>

                          <td className="text-end">
                            <strong style={{ color: '#fbbf24' }}>
                              {formatMoney(item.price_override || item.price || 0)}
                            </strong>
                          </td>

                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline-info"
                                onClick={() => {
                                  setEditItem(item);
                                  setShowModal(true);
                                }}
                              >
                                <FaEdit className="me-1" />
                                Edit
                              </Button>

                              <Button
                                size="sm"
                                variant="outline-warning"
                                onClick={() => resyncListing(item.listing_id)}
                                disabled={refreshingAction}
                              >
                                <FaSyncAlt className="me-1" />
                                Resync
                              </Button>

                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => archiveListing(item.listing_id)}
                                disabled={refreshingAction}
                              >
                                <FaTrash className="me-1" />
                                Archive
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-5">
                          <div style={{ color: '#f8fafc', fontWeight: 900, fontSize: 18 }}>
                            No listings found.
                          </div>
                          <div style={mutedStyle}>
                            Try clearing filters or changing the search.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {renderPagination()}
            </>
          )}
        </div>

        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          size="lg"
          centered
          className="listings-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Listing</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {editItem && (
              <Form>
                <div className="p-3 mb-3" style={cardSoftStyle}>
                  <Row className="g-2">
                    <Col md={4}>
                      <MiniMetric label="SKU" value={editItem.sku || '—'} color="#bfdbfe" />
                    </Col>

                    <Col md={4}>
                      <MiniMetric label="Platform" value={editItem.platform || '—'} color="#86efac" />
                    </Col>

                    <Col md={4}>
                      <MiniMetric
                        label="Current Price"
                        value={formatMoney(editItem.price_override || editItem.price || 0)}
                        color="#fbbf24"
                      />
                    </Col>
                  </Row>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Title Override</Form.Label>
                  <Form.Control
                    type="text"
                    value={editItem.title_override || ''}
                    onChange={(e) =>
                      setEditItem({
                        ...editItem,
                        title_override: e.target.value,
                      })
                    }
                    style={inputStyle}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description Override</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={editItem.description_override || ''}
                    onChange={(e) =>
                      setEditItem({
                        ...editItem,
                        description_override: e.target.value,
                      })
                    }
                    style={inputStyle}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Price Override</Form.Label>
                      <Form.Control
                        type="number"
                        value={editItem.price_override || ''}
                        onChange={(e) =>
                          setEditItem({
                            ...editItem,
                            price_override: e.target.value,
                          })
                        }
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        value={editItem.status}
                        onChange={(e) =>
                          setEditItem({
                            ...editItem,
                            status: e.target.value,
                          })
                        }
                        style={inputStyle}
                      >
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                        <option value="ARCHIVED">ARCHIVED</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Check
                  type="switch"
                  label="Active"
                  checked={!!editItem.active}
                  onChange={(e) =>
                    setEditItem({
                      ...editItem,
                      active: e.target.checked ? 1 : 0,
                    })
                  }
                  style={{ color: '#cbd5e1', fontWeight: 800 }}
                />
              </Form>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>

            <Button variant="primary" onClick={saveEdit} disabled={refreshingAction}>
              {refreshingAction ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <FaLayerGroup className="me-2" />
                  Save Changes
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default Listings;